import { IBadge } from '../../indigofair/badges/IBadge';
import { ColorTokenKey } from '../../indigofair/data/ColorTokenKey';
import { IAction } from '../../indigofair/data/IAction';
import { IApplicableMembershipBenefits } from '../../indigofair/data/IApplicableMembershipBenefits';
import { IAvatarTile } from '../../indigofair/data/IAvatarTile';
import { IBrandTile } from '../../indigofair/data/IBrandTile';
import { IBrandTileWithAction } from '../../indigofair/data/IBrandTileWithAction';
import { IButtonTile } from '../../indigofair/data/IButtonTile';
import { IImageGalleryTile } from '../../indigofair/data/IImageGalleryTile';
import { IImageTile } from '../../indigofair/data/IImageTile';
import { IInsiderLabel } from '../../indigofair/data/IInsiderLabel';
import { ILeanProductTile } from '../../indigofair/data/ILeanProductTile';
import { IMessageCardTile } from '../../indigofair/data/IMessageCardTile';
import { IPillTile } from '../../indigofair/data/IPillTile';
import { IProductWithBrandInfoTile } from '../../indigofair/data/IProductWithBrandInfoTile';
import { ITextTile } from '../../indigofair/data/ITextTile';
import { ITileLoggingData } from '../../indigofair/data/ITileLoggingData';

export namespace ITile {
  export namespace ISduiProductTile {
    export namespace ISduiProductTileBrandInfo {
      /**
       * Return an instance of ISduiProductTileBrandInfo with the provided fields. Any field not
       * listed will be populated with an empty value (an empty array,
       * or undefined, whichever is valid for the type of the value.)
       */
      export const build = (
        partial?: Partial<ISduiProductTileBrandInfo>
      ): ISduiProductTileBrandInfo =>
        ({
          ...(partial ?? {}),
        }) as ISduiProductTileBrandInfo;
    }

    export interface ISduiProductTileBrandInfo {
      /**
       * All insider benefits a viewing retailer gets for this brand based on their current or eligible Insider status.
       */
      insider_benefits: IApplicableMembershipBenefits | undefined;
      insider_label: IInsiderLabel | undefined;
      is_highlighted_brand_for_promotional_event: boolean | undefined;
    }

    /**
     * Return an instance of ISduiProductTile with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (
      partial?: Partial<ISduiProductTile>
    ): ISduiProductTile =>
      ({
        badges: [],
        ...(partial ?? {}),
      }) as ISduiProductTile;
  }

  /**
   * Web + App, added SDUI prefix to solve duplicate naming issue in `web-api`
   */
  export interface ISduiProductTile {
    product: ILeanProductTile | undefined;
    variant: keyof typeof ITile.ProductTileVariant | undefined;
    /**
     * Use `badgeList` field in `product` instead.
     * @deprecated
     */
    badges: Array<IBadge>;
    /**
     * Only carry over the minimum necessary info e.g insider free shipping
     * TODO: clean this field up by using a more proper `LeanProductTile`
     */
    brand_info: IBrandTile | undefined;
  }

  export type ProductTileVariantType =
    | 'PRODUCT_TILE_VARIANT_UNKNOWN'
    | 'DEFAULT_PRODUCT_TILE'
    | 'IMAGE_ONLY_TILE'
    | 'IMAGE_ONLY_TILE_WITH_PRICE'
    | 'EVALUATE_TILE'
    | 'DISCOVERY_TILE';

  export enum ProductTileVariant {
    PRODUCT_TILE_VARIANT_UNKNOWN = 'PRODUCT_TILE_VARIANT_UNKNOWN',
    /**
     * Tile with square product image and product name, App only
     * @deprecated
     */
    DEFAULT_PRODUCT_TILE = 'DEFAULT_PRODUCT_TILE',
    /**
     * Web + App
     */
    IMAGE_ONLY_TILE = 'IMAGE_ONLY_TILE',
    /**
     * Web + App - image only tile with price in bottom left corner
     * @deprecated
     */
    IMAGE_ONLY_TILE_WITH_PRICE = 'IMAGE_ONLY_TILE_WITH_PRICE',
    /**
     * Web + App - all tiles are from the same brand
     */
    EVALUATE_TILE = 'EVALUATE_TILE',
    /**
     * Web + App - tiles are from many brands
     */
    DISCOVERY_TILE = 'DISCOVERY_TILE',
  }

  export namespace ISduiBrandTile {
    /**
     * Return an instance of ISduiBrandTile with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (partial?: Partial<ISduiBrandTile>): ISduiBrandTile =>
      ({
        badges: [],
        ...(partial ?? {}),
      }) as ISduiBrandTile;
  }

  /**
   * Web + App, added SDUI prefix to solve duplicate naming issue in `web-api`
   */
  export interface ISduiBrandTile {
    brand: IBrandTile | undefined;
    /**
     * Use `badgeList` field in `brand` instead.
     * @deprecated
     */
    badges: Array<IBadge>;
    variant: keyof typeof ITile.SduiBrandTileVariant | undefined;
  }

  export type SduiBrandTileVariantType =
    | 'SDUI_BRAND_TILE_VARIANT_UNKNOWN'
    | 'DEFAULT_BRAND_TILE'
    | 'LARGE_BRAND_TILE';

  export enum SduiBrandTileVariant {
    SDUI_BRAND_TILE_VARIANT_UNKNOWN = 'SDUI_BRAND_TILE_VARIANT_UNKNOWN',
    /**
     * Tile designed for general use across carousels and grids. Supported by both web and apps.
     */
    DEFAULT_BRAND_TILE = 'DEFAULT_BRAND_TILE',
    /**
     * Tile designed for a single-column grid form factor. Intended for use for category brand-first pages for apps.
     */
    LARGE_BRAND_TILE = 'LARGE_BRAND_TILE',
  }

  export namespace ITileStyle {
    /**
     * Return an instance of ITileStyle with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (partial?: Partial<ITileStyle>): ITileStyle =>
      ({
        ...(partial ?? {}),
      }) as ITileStyle;
  }

  export interface ITileStyle {
    /**
     * Noted that this might be overrided by the specific tile level backgroundColor
     */
    background_color: keyof typeof ColorTokenKey | undefined;
    border_color: keyof typeof ColorTokenKey | undefined;
  }

  /**
   * Return an instance of ITile with the provided fields. Any field not
   * listed will be populated with an empty value (an empty array,
   * or undefined, whichever is valid for the type of the value.)
   */
  export const build = (partial?: Partial<ITile>): ITile =>
    ({
      on_view: [],
      on_click: [],
      ...(partial ?? {}),
    }) as ITile;
}

/**
 * Tile
 */
export interface ITile {
  on_view: Array<IAction>;
  on_click: Array<IAction>;
  logging_data: ITileLoggingData | undefined;
  style: ITile.ITileStyle | undefined;
  brand_tile: ITile.ISduiBrandTile | undefined;
  brand_tile_with_action: IBrandTileWithAction | undefined;
  product_with_brand_info_tile: IProductWithBrandInfoTile | undefined;
  product_tile: ITile.ISduiProductTile | undefined;
  button_tile: IButtonTile | undefined;
  image_tile: IImageTile | undefined;
  message_card_tile: IMessageCardTile | undefined;
  pill_tile: IPillTile | undefined;
  image_gallery_tile: IImageGalleryTile | undefined;
  avatar_tile: IAvatarTile | undefined;
  text_tile: ITextTile | undefined;
}
